import * as React from "react";
import { cn } from "@/lib/cn";

/**
 * A data table inside a card, with the scroll edge from `index.css`.
 *
 * `.table-scroll` is what makes a clipped ledger read as "scroll me" rather
 * than as truncated data. It belongs on the scroll CONTAINER, which is why
 * this component owns it instead of leaving it to each screen.
 */
export function TableShell({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("table-scroll overflow-x-auto", className)}>
      <table className="w-full min-w-[640px] border-collapse text-sm">{children}</table>
    </div>
  );
}

export function THead({ children }: { children: React.ReactNode }) {
  return (
    <thead className="border-b border-border-light">
      <tr className="text-left">{children}</tr>
    </thead>
  );
}

export function TH({
  children,
  className,
  numeric,
}: {
  children?: React.ReactNode;
  className?: string;
  numeric?: boolean;
}) {
  return (
    <th
      scope="col"
      className={cn(
        "whitespace-nowrap px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-muted-foreground",
        numeric && "text-right",
        className,
      )}
    >
      {children}
    </th>
  );
}

export function TBody({ children }: { children: React.ReactNode }) {
  return <tbody className="divide-y divide-border-light">{children}</tbody>;
}

export function TR({
  children,
  onClick,
  className,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  // A clickable row gets a real hover and a pointer. It is NOT given a
  // `role="button"`: the row's own link or button is what the keyboard reaches,
  // and announcing a table row as a button would be a lie to anybody not using
  // a mouse. Every clickable row here also carries a focusable control.
  return (
    <tr
      onClick={onClick}
      className={cn("transition-colors", onClick && "cursor-pointer hover:bg-hover", className)}
    >
      {children}
    </tr>
  );
}

export function TD({
  children,
  className,
  numeric,
  colSpan,
}: {
  children?: React.ReactNode;
  className?: string;
  numeric?: boolean;
  colSpan?: number;
}) {
  return (
    <td
      colSpan={colSpan}
      className={cn("px-4 py-2.5 align-middle", numeric && "text-right nums", className)}
    >
      {children}
    </td>
  );
}

/**
 * What a table says when it has nothing to say.
 *
 * A colSpan row rather than a block below the table, so the empty state sits
 * INSIDE the frame the operator is already looking at -- and so the header row
 * stays visible, which is what tells them the filter is the reason.
 */
export function TEmpty({ colSpan, children }: { colSpan: number; children: React.ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-muted-foreground">
        {children}
      </td>
    </tr>
  );
}
